import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import OutsideClickHandler from "react-outside-click-handler";
import Icon from "../../../components/Icon";
import { uploadCampaignAsset } from "../../../api/campaigns.service";
import { newBlock } from "./blocks";
import styles from "./BlockEditor.module.sass";

const EMOJIS = [
  "🎉", "🔥", "🚀", "🎯", "💪", "🧠", "📚", "🀄", "🇨🇳", "⭐",
  "🏆", "💎", "🎁", "⏰", "👋", "❤️", "😊", "🤓", "✨", "📈",
];

const BLOCK_MENU = [
  { type: "heading", label: "Título", icon: "H" },
  { type: "paragraph", label: "Texto", icon: "¶" },
  { type: "list", label: "Lista", icon: "•" },
  { type: "quote", label: "Cita", icon: "❝" },
  { type: "image", label: "Imagen", icon: "🖼" },
  { type: "button", label: "Botón", icon: "⬒" },
  { type: "divider", label: "Separador", icon: "—" },
];

const PLACEHOLDER = {
  heading: "Título de sección…",
  paragraph: "Escribe aquí…",
  quote: "Cita…",
  list: "Elemento de lista…",
};

/**
 * Área de texto enriquecido "uncontrolled": pinta el HTML inicial al montar y emite
 * el innerHTML en cada input. El padre la remonta (key) al cambiar idioma/bloque.
 */
const EditableHtml = ({ tag = "div", html, onChange, className, placeholder, disabled }) => {
  const ref = useRef(null);
  const [empty, setEmpty] = useState(!html);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.innerHTML = html || (tag === "ul" ? "<li></li>" : "");
    setEmpty(!html || !html.replace(/<[^>]*>/g, "").trim());
    // Solo al montar (uncontrolled): el padre usa key para remontar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => {
    if (!ref.current) return;
    const value = ref.current.innerHTML;
    const isEmpty = !value.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
    setEmpty(isEmpty);
    onChange(isEmpty ? "" : value);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    document.execCommand("insertText", false, e.clipboardData.getData("text/plain"));
    emit();
  };

  const Tag = tag;
  return (
    <div className={styles.editableWrap}>
      <Tag
        ref={ref}
        className={className}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={handlePaste}
      />
      {empty && placeholder && <div className={styles.placeholder}>{placeholder}</div>}
    </div>
  );
};

/**
 * Editor visual por bloques para el cuerpo del email. La estructura de bloques se
 * comparte entre idiomas; el texto se edita por idioma (prop `lang`). Soporta
 * arrastrar para reordenar y soltar imágenes (se suben a /messaging/assets).
 */
const BlockEditor = ({ blocks, onChange, lang, disabled, version = 0, onNotify }) => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [fileOver, setFileOver] = useState(false);
  const fileInputRef = useRef(null);
  const replaceTargetRef = useRef(null); // id del bloque imagen a reemplazar (o null = añadir)

  const update = (id, patch) =>
    onChange(blocks.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const updateText = (id, value) => {
    onChange(
      blocks.map((b) => (b.id === id ? { ...b, text: { ...b.text, [lang]: value } } : b))
    );
  };

  const remove = (id) => onChange(blocks.filter((b) => b.id !== id));

  const move = (from, to) => {
    if (to < 0 || to >= blocks.length || from === to) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const add = (type, index = blocks.length) => {
    if (type === "image") {
      replaceTargetRef.current = null;
      fileInputRef.current?.click();
      return;
    }
    const block = newBlock(type, type === "button" ? { text: { [lang]: "" } } : {});
    const next = [...blocks];
    next.splice(index, 0, block);
    onChange(next);
  };

  const uploadFiles = async (files, insertIndex = blocks.length) => {
    const images = [...files].filter((f) => f.type.startsWith("image/"));
    if (!images.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of images) {
        // eslint-disable-next-line no-await-in-loop
        const resp = await uploadCampaignAsset(file);
        if (resp?.url) uploaded.push(resp.url);
      }
      if (!uploaded.length) throw new Error("no url");
      if (replaceTargetRef.current) {
        update(replaceTargetRef.current, { url: uploaded[0] });
        replaceTargetRef.current = null;
      } else {
        const next = [...blocks];
        next.splice(insertIndex, 0, ...uploaded.map((url) => newBlock("image", { url })));
        onChange(next);
      }
    } catch (e) {
      onNotify?.({ severity: "error", msg: "No se pudo subir la imagen" });
    } finally {
      setUploading(false);
    }
  };

  const exec = (command, arg) => {
    if (disabled) return;
    document.execCommand(command, false, arg);
  };

  const insertLink = () => {
    const url = window.prompt("URL del enlace:", "https://");
    if (url) exec("createLink", url);
  };

  // Paleta acotada — la misma en el picker de texto y de fondo.
  const COLOR_PALETTE = [
    "#171725", "#4B5563", "#9CA3AF", "#FFFFFF",
    "#2A85FF", "#8E59FF", "#83BF6E", "#FFBC99",
    "#FF6A55", "#F4C542", "#38BDC3", "#EC407A",
  ];

  const [colorOpen, setColorOpen] = useState(false);
  const [bgOpen, setBgOpen] = useState(false);
  const [sizeOpen, setSizeOpen] = useState(false);
  const [alignOpen, setAlignOpen] = useState(false);

  const applyColor = (c) => { exec("foreColor", c); setColorOpen(false); };
  const applyBg = (c) => { exec("hiliteColor", c); setBgOpen(false); };
  const applySize = (px) => {
    if (disabled) return;
    // execCommand fontSize solo acepta 1..7 y queda tosco. Envuelvo la selección
    // en un span con font-size en px — email-safe y consistente entre bloques.
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const range = sel.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontSize = px + "px";
    try { range.surroundContents(span); } catch { /* selección mixta: ignora */ }
    setSizeOpen(false);
    // dispara input para que el bloque emita cambios
    const target = range.commonAncestorContainer.parentElement?.closest?.("[contenteditable]");
    target?.dispatchEvent(new Event("input", { bubbles: true }));
  };
  const applyAlign = (dir) => {
    if (disabled) return;
    exec(
      dir === "left" ? "justifyLeft"
      : dir === "center" ? "justifyCenter"
      : dir === "right" ? "justifyRight"
      : "justifyFull"
    );
    setAlignOpen(false);
  };

  const SIZE_OPTS = [
    { label: "Pequeño", px: 12 },
    { label: "Normal", px: 15 },
    { label: "Grande", px: 20 },
    { label: "Enorme", px: 28 },
  ];

  const tools = [
    { icon: <b>B</b>, title: "Negrita", action: () => exec("bold") },
    { icon: <i>I</i>, title: "Cursiva", action: () => exec("italic") },
    { icon: <u>S</u>, title: "Subrayado", action: () => exec("underline") },
    { icon: "🔗", title: "Enlace", action: insertLink },
    { icon: "✕", title: "Quitar formato", action: () => exec("removeFormat") },
  ];

  const renderBlock = (block) => {
    const key = `${block.id}:${lang}:${version}`;
    switch (block.type) {
      case "heading":
        return (
          <EditableHtml
            key={key}
            html={block.text[lang]}
            onChange={(v) => updateText(block.id, v)}
            className={cn(styles.editable, styles.heading)}
            placeholder={PLACEHOLDER.heading}
            disabled={disabled}
          />
        );
      case "paragraph":
        return (
          <EditableHtml
            key={key}
            html={block.text[lang]}
            onChange={(v) => updateText(block.id, v)}
            className={cn(styles.editable, styles.paragraph)}
            placeholder={PLACEHOLDER.paragraph}
            disabled={disabled}
          />
        );
      case "quote":
        return (
          <EditableHtml
            key={key}
            html={block.text[lang]}
            onChange={(v) => updateText(block.id, v)}
            className={cn(styles.editable, styles.quote)}
            placeholder={PLACEHOLDER.quote}
            disabled={disabled}
          />
        );
      case "list":
        return (
          <EditableHtml
            key={key}
            tag="ul"
            html={block.text[lang]}
            onChange={(v) => updateText(block.id, v)}
            className={cn(styles.editable, styles.list)}
            disabled={disabled}
          />
        );
      case "image": {
        const align = block.align || "center";
        const width = block.width || 100;
        return (
          <div className={styles.imageBlock} style={{ textAlign: align }}>
            {block.url ? (
              <img src={block.url} alt="" style={{ maxWidth: width + "%" }} />
            ) : (
              <div className={styles.imageEmpty}>Sin imagen</div>
            )}
            {!disabled && (
              <div className={styles.imageActions}>
                <button
                  type="button"
                  onClick={() => {
                    replaceTargetRef.current = block.id;
                    fileInputRef.current?.click();
                  }}
                >
                  Cambiar
                </button>
                <input
                  className={styles.imageUrlInput}
                  placeholder="…o pega una URL"
                  value={block.url}
                  onChange={(e) => update(block.id, { url: e.target.value })}
                />
                <div className={styles.imageChips}>
                  {[
                    { v: "left", label: "⇤" },
                    { v: "center", label: "≡" },
                    { v: "right", label: "⇥" },
                  ].map((a) => (
                    <button
                      key={a.v}
                      type="button"
                      className={cn(styles.imageChip, { [styles.imageChipActive]: align === a.v })}
                      onClick={() => update(block.id, { align: a.v })}
                      title={"Alinear " + a.v}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
                <div className={styles.imageChips}>
                  {[50, 75, 100].map((w) => (
                    <button
                      key={w}
                      type="button"
                      className={cn(styles.imageChip, { [styles.imageChipActive]: width === w })}
                      onClick={() => update(block.id, { width: w })}
                      title={"Ancho " + w + "%"}
                    >
                      {w}%
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }
      case "button":
        return (
          <div className={styles.buttonBlock}>
            <input
              key={key}
              className={styles.buttonLabel}
              placeholder={`Texto del botón (${lang.toUpperCase()})`}
              value={block.text[lang] || ""}
              disabled={disabled}
              onChange={(e) => updateText(block.id, e.target.value)}
            />
            <input
              className={styles.buttonUrl}
              placeholder="https://destino…"
              value={block.url}
              disabled={disabled}
              onChange={(e) => update(block.id, { url: e.target.value })}
            />
          </div>
        );
      case "divider":
        return <hr className={styles.dividerBlock} />;
      default:
        return null;
    }
  };

  return (
    <div
      className={cn(styles.editor, {
        [styles.disabled]: disabled,
        [styles.fileOver]: fileOver,
      })}
      onDragOver={(e) => {
        if (e.dataTransfer?.types?.includes("Files")) {
          e.preventDefault();
          setFileOver(true);
        }
      }}
      onDragLeave={() => setFileOver(false)}
      onDrop={(e) => {
        if (e.dataTransfer?.files?.length) {
          e.preventDefault();
          setFileOver(false);
          replaceTargetRef.current = null;
          uploadFiles(e.dataTransfer.files);
        }
      }}
    >
      <div className={styles.toolbar}>
        {tools.map((t) => (
          <button
            key={t.title}
            type="button"
            className={styles.tool}
            title={t.title}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={t.action}
          >
            {t.icon}
          </button>
        ))}

        {/* Color de texto */}
        <div className={styles.emojiWrap}>
          <button
            type="button"
            className={styles.tool}
            title="Color de texto"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setColorOpen((v) => !v); setBgOpen(false); setSizeOpen(false); setAlignOpen(false); }}
          >
            <span style={{ borderBottom: "3px solid #2A85FF", padding: "0 3px" }}>A</span>
          </button>
          {colorOpen && (
            <OutsideClickHandler onOutsideClick={() => setColorOpen(false)}>
              <div className={cn(styles.emojiPanel, styles.colorPanel)}>
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={styles.colorSwatch}
                    style={{ background: c }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyColor(c)}
                    title={c}
                  />
                ))}
              </div>
            </OutsideClickHandler>
          )}
        </div>

        {/* Color de fondo (highlight) */}
        <div className={styles.emojiWrap}>
          <button
            type="button"
            className={styles.tool}
            title="Color de fondo"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setBgOpen((v) => !v); setColorOpen(false); setSizeOpen(false); setAlignOpen(false); }}
          >
            <span style={{ background: "#FFD88D", padding: "0 4px", borderRadius: 3, color: "#1B202B" }}>H</span>
          </button>
          {bgOpen && (
            <OutsideClickHandler onOutsideClick={() => setBgOpen(false)}>
              <div className={cn(styles.emojiPanel, styles.colorPanel)}>
                <button
                  type="button"
                  className={styles.colorSwatch}
                  style={{ background: "transparent", border: "1px dashed rgba(255,255,255,.3)" }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applyBg("transparent")}
                  title="Sin fondo"
                />
                {COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={styles.colorSwatch}
                    style={{ background: c }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applyBg(c)}
                    title={c}
                  />
                ))}
              </div>
            </OutsideClickHandler>
          )}
        </div>

        {/* Tamaño de fuente */}
        <div className={styles.emojiWrap}>
          <button
            type="button"
            className={styles.tool}
            title="Tamaño de texto"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setSizeOpen((v) => !v); setColorOpen(false); setBgOpen(false); setAlignOpen(false); }}
          >
            aA
          </button>
          {sizeOpen && (
            <OutsideClickHandler onOutsideClick={() => setSizeOpen(false)}>
              <div className={cn(styles.emojiPanel, styles.sizePanel)}>
                {SIZE_OPTS.map((s) => (
                  <button
                    key={s.px}
                    type="button"
                    className={styles.sizeItem}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => applySize(s.px)}
                    style={{ fontSize: Math.min(18, s.px) + "px" }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </OutsideClickHandler>
          )}
        </div>

        {/* Alineación */}
        <div className={styles.emojiWrap}>
          <button
            type="button"
            className={styles.tool}
            title="Alineación"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => { setAlignOpen((v) => !v); setColorOpen(false); setBgOpen(false); setSizeOpen(false); }}
          >
            ≡
          </button>
          {alignOpen && (
            <OutsideClickHandler onOutsideClick={() => setAlignOpen(false)}>
              <div className={cn(styles.emojiPanel, styles.alignPanel)}>
                <button type="button" className={styles.sizeItem} onMouseDown={(e) => e.preventDefault()} onClick={() => applyAlign("left")}>⇤ Izquierda</button>
                <button type="button" className={styles.sizeItem} onMouseDown={(e) => e.preventDefault()} onClick={() => applyAlign("center")}>≡ Centro</button>
                <button type="button" className={styles.sizeItem} onMouseDown={(e) => e.preventDefault()} onClick={() => applyAlign("right")}>⇥ Derecha</button>
                <button type="button" className={styles.sizeItem} onMouseDown={(e) => e.preventDefault()} onClick={() => applyAlign("justify")}>☰ Justificado</button>
              </div>
            </OutsideClickHandler>
          )}
        </div>

        <div className={styles.emojiWrap}>
          <button
            type="button"
            className={styles.tool}
            title="Emoji"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setEmojiOpen((v) => !v)}
          >
            😊
          </button>
          {emojiOpen && (
            <OutsideClickHandler onOutsideClick={() => setEmojiOpen(false)}>
              <div className={styles.emojiPanel}>
                {EMOJIS.map((e) => (
                  <button
                    key={e}
                    type="button"
                    className={styles.emoji}
                    onMouseDown={(ev) => ev.preventDefault()}
                    onClick={() => {
                      exec("insertText", e);
                      setEmojiOpen(false);
                    }}
                  >
                    {e}
                  </button>
                ))}
              </div>
            </OutsideClickHandler>
          )}
        </div>
        <div className={styles.toolbarHint}>
          {uploading ? "Subiendo imagen…" : "Arrastra imágenes aquí para añadirlas"}
        </div>
      </div>

      <div className={styles.blocks}>
        {blocks.length === 0 && (
          <div className={styles.empty}>
            Añade bloques abajo o arrastra una imagen. El texto se escribe por idioma; las
            imágenes, botones y el orden se comparten entre todos.
          </div>
        )}
        {blocks.map((block, index) => (
          <div
            key={block.id}
            className={cn(styles.blockRow, {
              [styles.blockOver]: overIndex === index && dragIndex !== null,
            })}
            onDragOver={(e) => {
              if (dragIndex !== null) {
                e.preventDefault();
                setOverIndex(index);
              }
            }}
            onDrop={(e) => {
              if (dragIndex !== null) {
                e.preventDefault();
                move(dragIndex, index);
                setDragIndex(null);
                setOverIndex(null);
              }
            }}
          >
            {!disabled && (
              <div className={styles.blockSide}>
                <span
                  className={styles.dragHandle}
                  title="Arrastra para reordenar"
                  draggable
                  onDragStart={(e) => {
                    setDragIndex(index);
                    e.dataTransfer.effectAllowed = "move";
                    e.dataTransfer.setData("text/plain", String(index));
                  }}
                  onDragEnd={() => {
                    setDragIndex(null);
                    setOverIndex(null);
                  }}
                >
                  ⠿
                </span>
              </div>
            )}
            <div className={styles.blockBody}>{renderBlock(block)}</div>
            {!disabled && (
              <div className={styles.blockActions}>
                <button type="button" title="Subir" onClick={() => move(index, index - 1)}>
                  <Icon name="arrow-top" size="14" />
                </button>
                <button type="button" title="Bajar" onClick={() => move(index, index + 1)}>
                  <Icon name="arrow-bottom" size="14" />
                </button>
                <button
                  type="button"
                  title="Eliminar bloque"
                  className={styles.blockDelete}
                  onClick={() => remove(block.id)}
                >
                  <Icon name="trash" size="14" />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {!disabled && (
        <div className={styles.addBar}>
          {BLOCK_MENU.map((m) => (
            <button key={m.type} type="button" className={styles.addButton} onClick={() => add(m.type)}>
              <span className={styles.addIcon}>{m.icon}</span>
              {m.label}
            </button>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          uploadFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
};

export default BlockEditor;
